import { useRef, useCallback, useEffect } from "react";
import { useTranslations, useAppVariant, useToast } from "@jugl-web/utils";
import { MediaQualityOptions } from "@jugl-web/rest-api";
import { compressImage } from "../utils/compressImage";

export interface FileSelectConfig {
  maxFileSize?: number;
  multiple?: boolean;
  acceptTypes?: string;
  compressionConfig?: {
    image: MediaQualityOptions;
  };
}

const defaultConfig: FileSelectConfig = {
  multiple: false,
  maxFileSize: undefined,
  acceptTypes: "*/*",
};

export const useFileSelect = () => {
  const { t } = useTranslations();
  const { toast } = useToast({ variant: useAppVariant().variant });
  const $fileInputRef = useRef<HTMLInputElement | null>(null);

  // Create virtual input element
  useEffect(() => {
    if (!$fileInputRef.current) {
      const input = document.createElement("input");
      input.type = "file";
      input.style.cssText =
        "position: absolute; left: -10000px; visibility: hidden; opacity: 0;";
      document.body.appendChild(input);
      $fileInputRef.current = input;
    }

    const currentInput = $fileInputRef.current;
    return () => {
      if (currentInput && document.body.contains(currentInput)) {
        document.body.removeChild(currentInput);
      }
    };
  }, []);

  const compressFiles = useCallback(
    async (files: FileList | null | void, config: FileSelectConfig) => {
      if (!files) {
        return null;
      }
      if (!config.compressionConfig) {
        return Array.from(files);
      }

      const { compressionConfig } = config;

      const filesArray = Array.from(files);
      const compressedFilesPromises = filesArray.map((file: File) => {
        const isImage = file.type.includes("image");

        if (!isImage || !compressionConfig.image) {
          return Promise.resolve(file);
        }
        return new Promise<File>((resolve, reject) => {
          compressImage(file, compressionConfig.image)
            .then((compressedFile) => {
              resolve(compressedFile);
            })
            .catch(reject);
        });
      });

      return Promise.all(compressedFilesPromises);
    },
    []
  );

  const getFiles = useCallback(
    (config: FileSelectConfig) =>
      new Promise<FileList | null>((resolve, reject) => {
        const fileInput = $fileInputRef.current;
        if (!fileInput) {
          reject(new Error("File input not found"));
          return;
        }
        fileInput.multiple = config.multiple || false;
        fileInput.accept = config.acceptTypes || "*/*";

        const handleChange = async (event: Event) => {
          cleanup();
          const target = event.target as HTMLInputElement;
          if (!target.files || target.files.length === 0) {
            resolve(null);
            return;
          }
          const { maxFileSize } = config;

          if (typeof maxFileSize === "number") {
            const maxSizeInBytes = maxFileSize * 1024 * 1024;

            const hasTooLargeFile = Array.from(target.files).some(
              (file) => file.size > maxSizeInBytes
            );

            if (hasTooLargeFile) {
              toast(
                t(
                  {
                    id: "feedback.too-large-file-megabytes",
                    defaultMessage:
                      "The file is too large. The maximum size is {fileSize}MB",
                  },
                  { fileSize: maxFileSize }
                ),
                { variant: "error" }
              );

              reject(new Error("File too large"));
              return;
            }
          }

          resolve(target.files);
        };

        const handleCancel = () => {
          cleanup();
          resolve(null);
        };

        const cleanup = () => {
          fileInput.removeEventListener("change", handleChange);
          fileInput.removeEventListener("cancel", handleCancel);
        };

        fileInput.addEventListener("change", handleChange);
        fileInput.addEventListener("cancel", handleCancel);

        fileInput.click();
      }),
    [t, toast]
  );

  const selectFile = useCallback(
    async (config: FileSelectConfig = defaultConfig) => {
      config = { ...defaultConfig, ...config };
      if ($fileInputRef.current) {
        $fileInputRef.current.value = "";
      }

      return getFiles(config)
        .then((files) => compressFiles(files, config))
        .catch(() => null);
    },
    [compressFiles, getFiles]
  );

  return { selectFile };
};
